import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Newspaper,
    Pin,
    Plus,
    Trash2,
    Edit3,
    Send,
    Flame,
    AlertTriangle,
    Megaphone,
    Info,
    Quote,
    Loader2,
    Bell,
    X,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import FileUpload from "../../components/FileUpload";

const CATEGORIES = [
    { id: "info", label: "Info", color: "#7dd3fc", Icon: Info },
    { id: "campagne", label: "Campagne", color: "#a78bfa", Icon: Megaphone },
    { id: "temoignage", label: "Témoignage", color: "#fbbf24", Icon: Quote },
    { id: "urgence", label: "Urgence", color: "#ef4444", Icon: Flame },
    { id: "alerte", label: "Alerte", color: "#fb923c", Icon: AlertTriangle },
];

export default function NewsEditor({ associationId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // current item being created/edited

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/associations/${associationId}/news`);
            setItems(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [associationId]);

    const startCreate = () =>
        setEditing({
            title: "",
            body: "",
            category: "info",
            pinned: false,
            notify_push: false,
            attachment_url: "",
            image_url: "",
            expires_at: null,
            _new: true,
        });

    const save = async () => {
        const e = editing;
        if (!e.title.trim() || !e.body.trim()) {
            toast.error("Titre et corps sont requis");
            return;
        }
        try {
            if (e._new) {
                await api.post(`/admin/associations/${associationId}/news`, {
                    title: e.title,
                    body: e.body,
                    category: e.category,
                    pinned: e.pinned,
                    notify_push: e.notify_push,
                    attachment_url: e.attachment_url || null,
                    image_url: e.image_url || null,
                    expires_at: e.expires_at || null,
                });
                toast.success(
                    e.notify_push
                        ? "Actualité publiée + notification envoyée"
                        : "Actualité publiée",
                );
            } else {
                await api.patch(
                    `/admin/associations/${associationId}/news/${e.news_id}`,
                    {
                        title: e.title,
                        body: e.body,
                        category: e.category,
                        pinned: e.pinned,
                        attachment_url: e.attachment_url || null,
                        image_url: e.image_url || null,
                        expires_at: e.expires_at || null,
                    },
                );
                toast.success("Actualité mise à jour");
            }
            setEditing(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const remove = async (nid) => {
        if (!window.confirm("Supprimer cette actualité ?")) return;
        try {
            await api.delete(`/admin/associations/${associationId}/news/${nid}`);
            toast.success("Actualité supprimée");
            load();
        } catch {
            toast.error("Erreur");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                        Communication terrain
                    </p>
                    <h4 className="text-lg font-extrabold text-white">
                        Actualités ({items.length})
                    </h4>
                </div>
                <button
                    onClick={startCreate}
                    data-testid="news-create-btn"
                    className="inline-flex items-center gap-2 bg-shariti-orange text-white text-xs font-extrabold py-2 px-4 rounded-2xl hover:bg-shariti-orange-light transition-all"
                >
                    <Plus className="w-3.5 h-3.5" />
                    Nouvelle actualité
                </button>
            </div>

            {loading ? (
                <div className="text-center py-8 text-white/40">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-10 rounded-2xl border border-white/10">
                    <Newspaper className="w-7 h-7 text-white/40 mx-auto mb-3" />
                    <p className="text-sm text-white/55 italic">
                        Aucune actualité. Crée la première en cliquant sur le bouton ci-dessus.
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((n) => {
                        const cat = CATEGORIES.find((c) => c.id === n.category) || CATEGORIES[0];
                        const Icon = cat.Icon;
                        return (
                            <div
                                key={n.news_id}
                                className="rounded-2xl border border-white/10 bg-white/4 p-3.5"
                                data-testid={`news-row-${n.news_id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                                        <Icon className="w-4 h-4" style={{ color: cat.color }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span
                                                className="text-[9px] font-extrabold tracking-[0.18em] uppercase px-1.5 py-0.5 rounded-full"
                                                style={{
                                                    background: `${cat.color}24`,
                                                    color: cat.color,
                                                }}
                                            >
                                                {cat.label}
                                            </span>
                                            {n.pinned && (
                                                <Pin className="w-3 h-3 text-shariti-orange-light" />
                                            )}
                                            {n.notify_push_at_create && (
                                                <Bell className="w-3 h-3 text-blue-300" />
                                            )}
                                            <span className="text-[10px] text-white/40 ml-auto">
                                                {new Date(n.published_at).toLocaleDateString("fr-FR", {
                                                    day: "numeric",
                                                    month: "short",
                                                })}
                                            </span>
                                        </div>
                                        <h5 className="text-sm font-extrabold text-white mb-1 leading-tight">
                                            {n.title}
                                        </h5>
                                        <p className="text-[12px] text-white/60 font-light line-clamp-2">
                                            {n.body}
                                        </p>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <button
                                            onClick={() => setEditing(n)}
                                            className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5"
                                            data-testid={`news-edit-${n.news_id}`}
                                            aria-label="Éditer"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => remove(n.news_id)}
                                            className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10"
                                            data-testid={`news-delete-${n.news_id}`}
                                            aria-label="Supprimer"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
                    onClick={() => setEditing(null)}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-shariti-black border border-white/10 rounded-3xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="news-editor-modal"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                    {editing._new ? "Nouvelle actualité" : "Éditer l'actualité"}
                                </p>
                                <h3 className="text-xl font-extrabold text-white">
                                    {editing._new ? "Publier une info terrain" : editing.title}
                                </h3>
                            </div>
                            <button
                                onClick={() => setEditing(null)}
                                className="p-1.5 rounded-lg hover:bg-white/5"
                                aria-label="Fermer"
                            >
                                <X className="w-5 h-5 text-white/60" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Catégorie */}
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block mb-2">
                                    Catégorie
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {CATEGORIES.map((c) => {
                                        const Icon = c.Icon;
                                        const sel = editing.category === c.id;
                                        return (
                                            <button
                                                key={c.id}
                                                onClick={() => setEditing({ ...editing, category: c.id })}
                                                data-testid={`news-cat-${c.id}`}
                                                className={`inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-full transition-all border`}
                                                style={
                                                    sel
                                                        ? {
                                                              background: `${c.color}28`,
                                                              color: c.color,
                                                              borderColor: c.color,
                                                          }
                                                        : {
                                                              background: "rgba(255,255,255,0.04)",
                                                              color: "rgba(255,255,255,0.55)",
                                                              borderColor: "rgba(255,255,255,0.1)",
                                                          }
                                                }
                                            >
                                                <Icon className="w-3 h-3" />
                                                {c.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Titre */}
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block mb-1.5">
                                    Titre
                                </label>
                                <input
                                    type="text"
                                    value={editing.title}
                                    onChange={(e) =>
                                        setEditing({ ...editing, title: e.target.value })
                                    }
                                    placeholder="Ex: Campagne contre la peine de mort en Iran"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/40"
                                    data-testid="news-title-input"
                                />
                            </div>

                            {/* Corps */}
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block mb-1.5">
                                    Message
                                </label>
                                <textarea
                                    rows={5}
                                    value={editing.body}
                                    onChange={(e) =>
                                        setEditing({ ...editing, body: e.target.value })
                                    }
                                    placeholder="Le contexte, l'urgence, l'angle terrain à utiliser..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/40 resize-y"
                                    data-testid="news-body-input"
                                />
                            </div>

                            {/* Lien externe */}
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block mb-1.5">
                                    Lien externe (optionnel)
                                </label>
                                <input
                                    type="url"
                                    value={editing.attachment_url || ""}
                                    onChange={(e) =>
                                        setEditing({ ...editing, attachment_url: e.target.value })
                                    }
                                    placeholder="https://..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-white text-xs outline-none focus:border-shariti-orange/40"
                                />
                            </div>

                            {/* Iter-27.63 — Média d'illustration (optionnel) — pas de selfie recorder
                                ici : il vit dans /admin/espace-partenaires pour les messages de gratitude. */}
                            <div>
                                <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block mb-1.5">
                                    Image ou vidéo d'illustration (optionnel)
                                </label>
                                {editing.image_url ? (
                                    <div
                                        className="rounded-2xl overflow-hidden border border-shariti-yellow/30 bg-black"
                                        data-testid="news-media-preview"
                                    >
                                        {/\.(mp4|webm|mov)(\?|$)/i.test(editing.image_url) ? (
                                            <video
                                                src={editing.image_url}
                                                controls
                                                playsInline
                                                className="w-full max-h-56 object-cover bg-black"
                                            />
                                        ) : (
                                            <img
                                                src={editing.image_url}
                                                alt=""
                                                className="w-full max-h-56 object-cover"
                                            />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setEditing({ ...editing, image_url: "" })
                                            }
                                            data-testid="news-media-clear"
                                            className="w-full text-[11px] font-bold text-red-300 hover:text-red-200 bg-red-500/10 hover:bg-red-500/15 py-2 transition-all inline-flex items-center justify-center gap-1.5"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                            Retirer le média
                                        </button>
                                    </div>
                                ) : (
                                    <AdminNewsImageInput
                                        title={editing.title}
                                        body={editing.body}
                                        category={editing.category}
                                        onPicked={(url) =>
                                            setEditing({ ...editing, image_url: url })
                                        }
                                    />
                                )}
                            </div>

                            {/* Toggles */}
                            <div className="grid grid-cols-2 gap-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editing.pinned}
                                        onChange={(e) =>
                                            setEditing({ ...editing, pinned: e.target.checked })
                                        }
                                        data-testid="news-pinned-toggle"
                                        className="w-4 h-4 accent-shariti-orange"
                                    />
                                    <span className="text-xs text-white/70">
                                        <Pin className="w-3 h-3 inline mr-1" />
                                        Épingler en haut
                                    </span>
                                </label>
                                {editing._new && (
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editing.notify_push}
                                            onChange={(e) =>
                                                setEditing({
                                                    ...editing,
                                                    notify_push: e.target.checked,
                                                })
                                            }
                                            data-testid="news-notify-toggle"
                                            className="w-4 h-4 accent-shariti-orange"
                                        />
                                        <span className="text-xs text-white/70">
                                            <Bell className="w-3 h-3 inline mr-1" />
                                            Notifier les recruteurs
                                        </span>
                                    </label>
                                )}
                            </div>

                            {/* Bouton save */}
                            <button
                                onClick={save}
                                data-testid="news-save-btn"
                                className="w-full inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                            >
                                <Send className="w-4 h-4" />
                                {editing._new
                                    ? editing.notify_push
                                        ? "Publier + Notifier"
                                        : "Publier l'actualité"
                                    : "Mettre à jour"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
}


/**
 * Iter-30.30 — Composant équivalent à NewsImageInput côté Asso mais utilisant
 * l'endpoint admin (/api/admin/news/generate-image). Présenté à l'admin dans
 * NewsEditor à la place du simple FileUpload.
 */
function AdminNewsImageInput({ title, body, category, onPicked }) {
    const [generating, setGenerating] = useState(false);
    const titleReady = (title || "").trim().length >= 5;

    const generate = async () => {
        if (!titleReady) {
            toast.error("Renseigne un titre (5 caractères min.) pour générer une image cohérente");
            return;
        }
        setGenerating(true);
        const toastId = toast.loading("Génération en cours… (≈ 15-30 s)");
        try {
            const r = await api.post("/admin/news/generate-image", {
                title,
                body: body || "",
                category: category || "info",
            });
            const url = r.data?.image_url;
            if (!url) throw new Error("Pas d'URL retournée");
            onPicked(url);
            toast.success("Image générée !", { id: toastId });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur génération IA", { id: toastId });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="space-y-2.5" data-testid="admin-news-image-input">
            <button
                type="button"
                onClick={generate}
                disabled={generating || !titleReady}
                data-testid="admin-news-generate-image-btn"
                className={`w-full inline-flex items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-all text-left border ${
                    generating
                        ? "bg-shariti-yellow/10 border-shariti-yellow/30 cursor-wait"
                        : titleReady
                          ? "bg-shariti-yellow/8 border-shariti-yellow/30 hover:bg-shariti-yellow/15 hover:border-shariti-yellow/50"
                          : "bg-white/3 border-white/10 opacity-60 cursor-not-allowed"
                }`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-shariti-yellow/20 border border-shariti-yellow/40 flex items-center justify-center shrink-0">
                        {generating ? (
                            <Loader2 className="w-4 h-4 text-shariti-yellow animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4 text-shariti-yellow" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <p className="text-[13px] font-extrabold text-white leading-tight">
                            {generating ? "Génération en cours…" : "Générer une image avec l'IA"}
                        </p>
                        <p className="text-[10.5px] text-white/55 leading-tight mt-0.5 line-clamp-1">
                            {titleReady
                                ? "Image cover photo-réaliste à partir du titre + corps"
                                : "Renseigne d'abord le titre"}
                        </p>
                    </div>
                </div>
                {!generating && (
                    <span className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-shariti-yellow shrink-0">
                        Nano Banana
                    </span>
                )}
            </button>
            <div className="flex items-center gap-2 my-1">
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/35">
                    ou
                </span>
                <div className="flex-1 h-px bg-white/5" />
            </div>
            <FileUpload
                kind="image"
                currentUrl=""
                onUploaded={onPicked}
                testId="news-media-upload"
            />
        </div>
    );
}
